// Party-related utilities and constants
// Single source of truth for all party color mappings and helper functions

// Party color mapping - used across the entire application
export const partyColors: Record<string, string> = {
  // Left-wing
  'PC': '#991b1b',      // Partido Comunista
  'FA': '#0d9488',      // Frente Amplio (Coalition)
  'RD': '#a855f7',      // Revolución Democrática (in FA)
  'CS': '#059669',      // Convergencia Social (in FA)
  'FRVS': '#f43f5e',    // Federación Regionalista Verde Social
  'PAH': '#6366f1',     // Partido Humanista (alias for PH)
  'PH': '#6366f1',      // Partido Humanista

  // Center-left
  'PS': '#dc2626',      // Partido Socialista
  'PPD': '#f97316',     // Partido por la Democracia
  'PR': '#16a34a',      // Partido Radical
  'LIBERAL': '#eab308', // Partido Liberal (alias for PL)
  'PL': '#eab308',      // Partido Liberal

  // Center
  'DC': '#0891b2',      // Democracia Cristiana
  'DEM': '#06b6d4',     // Demócratas
  'AMA': '#d97706',     // Amarillos por Chile

  // Independents
  'IND': '#6b7280',

  // Right-wing
  'EVOP': '#7c3aed',     // Evolución Política
  'EVOPOLI': '#7c3aed',  // Evolución Política (alternative)
  'RN': '#2563eb',      // Renovación Nacional
  'UDI': '#1e40af',     // Unión Demócrata Independiente
  'PSC': '#ca8a04',     // Partido Social Cristiano
  'PREP': '#312e81',    // Partido Republicano
  'REP': '#312e81',     // Partido Republicano (alternative)
  'PNL': '#facc15',     // Partido Nacional Libertario

  // Other
  'PDG': '#8e44ad',     // Partido de la Gente
  'INDEP': '#6b7280',   // Independiente
};

// Full party names mapping
export const partyFullNames: Record<string, string> = {
  // Left-wing
  'PC': 'Partido Comunista',
  'FA': 'Frente Amplio',
  'RD': 'Revolución Democrática',
  'CS': 'Convergencia Social',
  'FRVS': 'Federación Regionalista Verde Social',
  'PAH': 'Partido Humanista',
  'PH': 'Partido Humanista',

  // Center-left
  'PS': 'Partido Socialista',
  'PPD': 'Partido por la Democracia',
  'PR': 'Partido Radical',
  'LIBERAL': 'Partido Liberal',
  'PL': 'Partido Liberal',

  // Center
  'DC': 'Democracia Cristiana',
  'DEM': 'Demócratas Chile',
  'AMA': 'Amarillos por Chile',

  // Independents
  'IND': 'Independiente',

  // Right-wing
  'EVOP': 'Evolución Política',
  'EVOPOLI': 'Evolución Política',
  'RN': 'Renovación Nacional',
  'UDI': 'Unión Demócrata Independiente',
  'PSC': 'Partido Social Cristiano',
  'PREP': 'Partido Republicano',
  'REP': 'Partido Republicano',
  'PNL': 'Partido Nacional Libertario',

  // Other
  'PDG': 'Partido de la Gente',
  'INDEP': 'Independiente',
};

// Party political spectrum classification
export const partySpectrum: Record<string, 'left' | 'center-left' | 'center' | 'center-right' | 'right' | 'independent'> = {
  // Left-wing
  'PC': 'left',
  'FA': 'left',
  'RD': 'left',
  'CS': 'left',
  'FRVS': 'left',
  'PAH': 'left',
  'PH': 'left',

  // Center-left
  'PS': 'center-left',
  'PPD': 'center-left',
  'PR': 'center-left',
  'LIBERAL': 'center-left',
  'PL': 'center-left',

  // Center
  'DC': 'center',
  'DEM': 'center',
  'AMA': 'center',

  // Independents
  'IND': 'independent',

  // Right-wing
  'EVOP': 'right',
  'EVOPOLI': 'right',
  'RN': 'right',
  'UDI': 'right',
  'PSC': 'right',
  'PREP': 'right',
  'REP': 'right',
  'PNL': 'right',

  // Other
  'PDG': 'center',
  'INDEP': 'independent',
};

/**
 * Get the color associated with a political party
 * @param party - Party abbreviation (e.g., 'PC', 'UDI', 'PS')
 * @returns Hex color code for the party, or default gray if not found
 */
export const getPartyColor = (party: string): string => {
  return partyColors[party] || '#6b7280'; // Default to gray for unknown parties
};

/**
 * Get the full name of a political party
 * @param party - Party abbreviation
 * @returns Full party name, or the abbreviation if not found
 */
export const getPartyFullName = (party: string): string => {
  return partyFullNames[party] || party;
};

/**
 * Get the political spectrum position of a party
 * @param party - Party abbreviation
 * @returns Political spectrum position
 */
export const getPartySpectrum = (party: string): string => {
  return partySpectrum[party] || 'independent';
};

/**
 * Check if a party is from the left wing
 * @param party - Party abbreviation
 */
export const isLeftWing = (party: string): boolean => {
  return partySpectrum[party] === 'left' || partySpectrum[party] === 'center-left';
};

/**
 * Check if a party is from the right wing
 * @param party - Party abbreviation
 */
export const isRightWing = (party: string): boolean => {
  return partySpectrum[party] === 'right' || partySpectrum[party] === 'center-right';
};

/**
 * Get all unique parties from the mapping
 * @returns Array of party abbreviations
 */
export const getAllParties = (): string[] => {
  return Object.keys(partyFullNames);
};

/**
 * Get parties grouped by political spectrum
 * @returns Object with parties grouped by spectrum position
 */
export const getPartiesBySpectrum = (): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {
    'left': [],
    'center-left': [],
    'center': [],
    'center-right': [],
    'right': [],
    'independent': [],
  };

  Object.entries(partySpectrum).forEach(([party, spectrum]) => {
    grouped[spectrum].push(party);
  });

  return grouped;
};

/**
 * Format party name for display (with full name in parentheses if different)
 * @param party - Party abbreviation
 * @returns Formatted party name
 */
export const formatPartyDisplay = (party: string): string => {
  const fullName = getPartyFullName(party);
  if (fullName === party) {
    return party;
  }
  return `${party} (${fullName})`;
};
